import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GeneratedContent } from "@/types/content";
import { TwitterPreview } from "./previews/TwitterPreview";
import { LinkedInPreview } from "./previews/LinkedInPreview";
import { BlogPreview } from "./previews/BlogPreview";
import { VideoPreview } from "./previews/VideoPreview";
import { PodcastPreview } from "./previews/PodcastPreview";
import { ContentReviewEditor } from "./ContentReviewEditor";
import { PencilLine, Eye, Copy, Check } from "lucide-react";
import { cn, copyFormattedContent } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ContentEditorProps {
  content: GeneratedContent;
  onContentChange: (content: string) => void;
  reviewedContent?: string;
  onReviewedContentChange?: (content: string) => void;
}

export const ContentEditor = ({ 
  content, 
  onContentChange,
  reviewedContent,
  onReviewedContentChange
}: ContentEditorProps) => {
  const [activeTab, setActiveTab] = useState<string>("preview");
  const [copied, setCopied] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<'version1' | 'version2'>('version1');
  const { toast } = useToast();

  const handleCopy = () => {
    const contentToCopy = previewVersion === 'version2' && reviewedContent 
      ? reviewedContent 
      : content.content;
    
    if (!contentToCopy || contentToCopy.trim() === '') {
      toast({
        title: "Error",
        description: "No content to copy",
        variant: "destructive"
      });
      return;
    }
    
    copyFormattedContent(contentToCopy, {
      onSuccess: () => {
        setCopied(true);
        toast({
          title: "Success",
          description: "Content copied to clipboard",
          variant: "success"
        });
        setTimeout(() => setCopied(false), 2000);
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to copy content",
          variant: "destructive"
        });
      }
    });
  };

  const renderPreview = () => {
    // Determine what content to use, with fallback to original content if needed
    const contentToShow = (previewVersion === 'version2' && reviewedContent && reviewedContent.trim() !== '')
      ? reviewedContent 
      : content.content;
    
    // Create a preview content object that includes the correct content
    const previewContent = {
      ...content,
      content: contentToShow,
    };

    switch (content.platform) {
      case 'twitter':
        return <TwitterPreview content={previewContent} />;
      case 'linkedin':
        return <LinkedInPreview content={previewContent} />;
      case 'blog':
        return <BlogPreview content={previewContent} />;
      case 'video':
        return <VideoPreview content={previewContent} />;
      case 'podcast':
        return <PodcastPreview content={previewContent} />;
      default:
        return <div className="p-4 whitespace-pre-wrap">{previewContent.content}</div>;
    }
  };

  return (
    <Tabs
      defaultValue="preview"
      value={activeTab}
      onValueChange={setActiveTab}
      className="w-full h-full flex flex-col"
    >
      <div className="flex items-center justify-between mb-4">
        <TabsList className="flex justify-center p-1 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg border border-blue-500/20">
          <TabsTrigger 
            value="edit" 
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2.5 transition-all duration-200",
              "data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20",
              "data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-300",
              "data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/10",
              "rounded-md"
            )}
          >
            <PencilLine className="h-4 w-4" />
            Edit
          </TabsTrigger>
          <TabsTrigger 
            value="preview"
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2.5 transition-all duration-200",
              "data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-blue-500/20",
              "data-[state=active]:text-cyan-700 dark:data-[state=active]:text-cyan-300",
              "data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/10",
              "rounded-md"
            )}
          >
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
        </TabsList>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          className="flex items-center gap-1 border-blue-200 hover:bg-blue-50 hover:text-blue-700 transition-colors"
        >
          {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
          <span>{copied ? "Copied!" : "Copy"}</span>
        </Button>
      </div>
      
      <TabsContent value="edit" className="mt-2 flex-1 overflow-hidden">
        <ContentReviewEditor 
          content={content.content} 
          onChange={onContentChange}
          platform={content.platform}
          metadata={{
            ...content.metadata,
            id: content.id
          }}
          improvedContent={reviewedContent}
          onImprovedContentChange={onReviewedContentChange}
        />
      </TabsContent>
      <TabsContent value="preview" className="mt-2 flex-1 overflow-auto">
        {reviewedContent && reviewedContent.trim() !== '' && (
          <div className="flex items-center justify-between mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
            <div className="text-blue-800">
              {previewVersion === 'version2' 
                ? "Showing Version 2 (reviewed content)" 
                : "Version 2 (reviewed content) is available"}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 text-xs border border-blue-200 bg-white hover:bg-blue-100"
              onClick={() => setPreviewVersion(
                previewVersion === 'version1' ? 'version2' : 'version1'
              )}
            >
              Switch to {previewVersion === 'version1' ? 'Version 2' : 'Version 1'}
            </Button>
          </div>
        )}
        <div className="overflow-auto h-full pb-4">
          {renderPreview()}
        </div>
      </TabsContent>
    </Tabs>
  );
};
